import { useEffect, useState } from 'react';
import {
  Eyebrow,
  Frame,
  Page,
  Sidebar,
  SideNav,
  Stack,
} from '@servicetitan/design-system';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import Navbar from './components/NavBar/NavBar';
import { permissionSets, RequirePermissions } from '@app/permissions';
import paths from '@app/routes/paths';
import classNames from 'classnames';
import styles from './MainLayout.module.css';
import PersonalSettingsIcon from '@assets/personal-settings.svg';
import Droppable from '@components/DragAndDrop/components/Droppable/Droppable';
import IncomingCallToastsGroup from '@modules/ToastModule/components/IncomingCallToast/IncomingCallToastGroup';
import { E911AddressBar } from './components/E911AddressBar/E911AddressBar';
import { userE911AddressState } from '@modules/E911AddressModule/store';
import { useSnapshot } from 'valtio';
import { FeatureFlagKeys, useFeatureFlags } from '@modules/FeatureFlagModule';
import useAuth from '@modules/AuthModule/hooks/useAuth';
import { useVirtualAgentFeatureGate } from '@api/hooks/useVirtualAgentFeatureGate';
import HiddenFrame from './components/HiddenFrame/HiddenFrame';
import DebugPanel from './components/DebugPanel/DebugPanel';
import CallBarManager from './components/CallBarManager/CallBarManager';
import UrgentFollowUpsBadge from '@modules/UrgentFollowUpsModule/components/UrgentFollowUpsBadge';
import { mainLayoutLocators } from '@locators/main-layout';
import { useAbandonedCallsCountQuery } from '@modules/UrgentFollowUpsModule/AbandonedCallsModule';
import { useSecondChanceLeadsCountQuery } from '@modules/UrgentFollowUpsModule/SecondChanceLeadsModule';
import {
  CALL_CENTER_MONITORING,
  QUEUE_MONITORING,
} from '../../../constants/labels';
import { useVirtualAgentFollowUpsCountQuery } from '@modules/UrgentFollowUpsModule/VirtualAgentFollowUpsModule';
import BrazeModule from '@modules/BrazeModule';
import { OutboundCallButton } from '@app/layouts/MainLayout/components/OutboundCall';
import { getAbandonedCallsBadgeCountFilters } from '@pages/urgentfollowups/AbandonedCallsPage/utils/filters';
import { getSecondChanceLeadsBadgeCountFilters } from '@pages/urgentfollowups/SecondChanceLeadsPage/utils/filters';
import { getVirtualAgentFollowUpsBadgeCountFilters } from '@pages/urgentfollowups/VirtualAgentFollowUpsPage/utils/filters';
import LoadingOverlay from '@components/LoadingOverlay/LoadingOverlay';
import useUserStatus from '@modules/UserStatusModule/hooks/useUserStatus';
import { useFrontEndSettings } from '@modules/FrontEndSettingsModule/hooks/useFrontEndSettings';
import { LoadingIndicator } from '@components/LoadingIndicator';
import { getTestIdProps } from '@lib/testUtils';
import ManagerAssistChat from '@components/ManagerAssist/ManagerAssistChat';
import { useGlobalRecordingOverride } from '@api/queries/recording/hooks/useGlobalRecordingOverride';
import {
  FeatureErrorBoundary,
  ModuleErrorBoundary,
} from '@components/ErrorBoundary';
import PendoModule, { PendoEvents } from '@modules/PendoModule';

const MainLayout = () => {
  const e911AddressState = useSnapshot(userE911AddressState);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const {
    [FeatureFlagKeys.featureManagerAssist]: featureManagerAssist,
    [FeatureFlagKeys.featureAbandonedCallFollowUps]:
      featureAbandonedCallFollowUps,
    [FeatureFlagKeys.featureSecondChanceLeadFollowUps]:
      featureSecondChanceLeadFollowUps,
    [FeatureFlagKeys.featureVoicemailFollowUps]: featureVoicemailFollowUps,
    [FeatureFlagKeys.featureDataIntegrationEnabled]:
      featureDataIntegrationEnabled,
    [FeatureFlagKeys.featureInboxTaskCardSettingsEnabled]:
      featureInboxTaskCardSettingsEnabled,
    [FeatureFlagKeys.featureManageAccounts]: featureManageAccounts,
    [FeatureFlagKeys.featureRecordingDefaultsConfiguration]:
      featureRecordingDefaultsConfiguration,
    [FeatureFlagKeys.featureUserRecordingDefaultsConfiguration]:
      featureUserRecordingDefaultsConfiguration,
    [FeatureFlagKeys.featureLiveCallCenterMonitoringPage]:
      featureLiveCallCenterMonitoringPage,
    [FeatureFlagKeys.featureGlobalRecordingOverridesEnabled]:
      featureGlobalRecordingOverridesEnabled,
    [FeatureFlagKeys.featureQueueManagementPage]: featureQueueManagementPage,
    [FeatureFlagKeys.featureVoiceAgentReporting]: featureVoiceAgentReporting,
  } = useFeatureFlags();
  const { refreshUserStatusLoading } = useUserStatus();
  const { isGlobalOverrideEnabled } = useGlobalRecordingOverride();
  const virtualAgentFeatures = useVirtualAgentFeatureGate();

  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [isManagerAssistActive, setIsManagerAssistActive] = useState(false);
  const isManagerAssistEnabled = !!featureManagerAssist;

  const isSettings = pathname.startsWith(`/settings`);
  const isActive = (path: string): boolean =>
    pathname === path || pathname.startsWith(`${path}/`);

  const handleToggleIsManagerAssistActive = () => {
    setIsManagerAssistActive(!isManagerAssistActive);
  };

  const handleToggleDebug = () => {
    setShowDebugPanel(!showDebugPanel);
  };

  const frontEndSettings = useFrontEndSettings();
  // TODO: Only show once settings are ready *and* SCL leads count query has
  // *also* finished.
  //
  // It would mean refactoring the UrgentFollowUpsBadge to take in the results
  // of the different *CountQuery hooks, rather than the hooks themselves, so
  // not doing it for now.
  //
  // It's only noticeable on the rare occasion the count query takes >500ms.
  const showSclBadge = frontEndSettings.followUps.secondChanceLeads.isReady;
  const getSecondChanceLeadBadgeFilters = () => {
    return getSecondChanceLeadsBadgeCountFilters(
      frontEndSettings.followUps.secondChanceLeads.daysUntilCold,
    );
  };

  const urgentFollowUpsEnabled =
    !!featureAbandonedCallFollowUps ||
    !!featureSecondChanceLeadFollowUps ||
    !!virtualAgentFeatures.followUps ||
    !!featureVoicemailFollowUps;

  // maintains loading state for the page outlet
  const outletLoading = refreshUserStatusLoading;

  useEffect(() => {
    BrazeModule.logPageLoadedEvent(pathname);
    PendoModule.trackEvent(PendoEvents.PAGE_LOADED, { path: pathname });
  }, [pathname]);

  return (
    <Droppable>
      <Frame
        className={classNames('h-100', styles.mainPageFrame)}
        header={
          <Navbar
            onToggleDebug={handleToggleDebug}
            isManagerAssistActive={isManagerAssistActive}
            onToggleIsManagerAssistActive={handleToggleIsManagerAssistActive}
          />
        }
      >
        {showDebugPanel && (
          <FeatureErrorBoundary featureName='Debug Panel'>
            <DebugPanel />
          </FeatureErrorBoundary>
        )}
        <Page
          className={classNames('main-layout', styles.mainPage)}
          maxWidth='wide'
          spacing='none'
          sidebar={
            <Sidebar
              localStorageKey='layout.main.sidebar'
              className={styles.Sidebar}
            >
              <SideNav
                className={classNames({
                  [styles.sideNavSettings]: isSettings,
                })}
                title={isSettings ? 'Settings' : undefined}
              >
                {!isSettings && (
                  <>
                    <Stack className='m-t-1 m-b-2'>
                      <FeatureErrorBoundary featureName='Outbound Call Button'>
                        <OutboundCallButton />
                      </FeatureErrorBoundary>
                    </Stack>

                    <Link to='/'>
                      <SideNav.Item
                        id={mainLayoutLocators.sidenav.inboxPageNavButton}
                        active={isActive('/')}
                      >
                        Inbox
                      </SideNav.Item>
                    </Link>
                    {featureLiveCallCenterMonitoringPage ? (
                      <RequirePermissions
                        permissions={permissionSets.general.QUEUE_MONITORING}
                        permissionsMatch='any'
                      >
                        <Link to={paths.callCenterMonitoring}>
                          <SideNav.Item
                            active={
                              isActive(paths.callCenterMonitoring) ||
                              isActive(paths.queuesMonitoring)
                            }
                            {...getTestIdProps(
                              mainLayoutLocators.sidenav
                                .callCenterMonitoringPageNavBtn,
                            )}
                          >
                            {CALL_CENTER_MONITORING}
                          </SideNav.Item>
                        </Link>
                      </RequirePermissions>
                    ) : (
                      <RequirePermissions
                        permissions={permissionSets.general.QUEUE_MONITORING}
                        permissionsMatch='any'
                      >
                        <Link to={paths.queues}>
                          <SideNav.Item
                            active={isActive(paths.queues)}
                            id={
                              mainLayoutLocators.sidenav
                                .queueMonitoringPageNavBtn
                            }
                          >
                            {QUEUE_MONITORING}
                          </SideNav.Item>
                        </Link>
                      </RequirePermissions>
                    )}

                    <Link to='/conversations'>
                      <SideNav.Item
                        active={isActive('/conversations')}
                        id={
                          mainLayoutLocators.sidenav
                            .conversationHistoryPageNavBtn
                        }
                      >
                        Conversation History
                      </SideNav.Item>
                    </Link>
                    {urgentFollowUpsEnabled && (
                      <SideNav.Item
                        active={isActive('/urgentfollowups/abandonedcalls')}
                        collapsible={{ expanded: true }}
                      >
                        <span className={styles.NoBoldExpanded}>
                          Urgent Follow Ups
                        </span>
                        <SideNav>
                          {!!featureAbandonedCallFollowUps && (
                            <RequirePermissions
                              permissions={
                                permissionSets.general.FOLLOW_UPS
                                  .ABANDONED_CALLS
                              }
                              permissionsMatch='all'
                            >
                              <Link
                                to='/urgentfollowups/abandonedcalls'
                                // FIXME: `to` isn't being respected
                                onClick={() =>
                                  navigate('/urgentfollowups/abandonedcalls')
                                }
                              >
                                <SideNav.Item
                                  active={isActive(
                                    '/urgentfollowups/abandonedcalls',
                                  )}
                                  id={
                                    mainLayoutLocators.sidenav
                                      .abandonedCallsPageNavBtn
                                  }
                                >
                                  Abandoned Calls
                                  <UrgentFollowUpsBadge
                                    id={
                                      mainLayoutLocators.sidenav
                                        .abandonedCallsBadge
                                    }
                                    badgeTitle='Number of new open abandoned calls'
                                    useCountQuery={useAbandonedCallsCountQuery}
                                    getFilters={
                                      getAbandonedCallsBadgeCountFilters
                                    }
                                  />
                                </SideNav.Item>
                              </Link>
                            </RequirePermissions>
                          )}
                          {!!featureSecondChanceLeadFollowUps && (
                            <RequirePermissions
                              permissions={
                                permissionSets.general.FOLLOW_UPS
                                  .SECOND_CHANCE_LEADS
                              }
                              permissionsMatch='all'
                            >
                              <Link
                                to='/urgentfollowups/secondchanceleads'
                                onClick={() =>
                                  navigate('/urgentfollowups/secondchanceleads')
                                }
                              >
                                <SideNav.Item
                                  active={isActive(
                                    '/urgentfollowups/secondchanceleads',
                                  )}
                                  id={
                                    mainLayoutLocators.sidenav
                                      .secondChanceLeadsPageNavBtn
                                  }
                                >
                                  Second Chance Leads
                                  {showSclBadge && (
                                    <UrgentFollowUpsBadge
                                      id={
                                        mainLayoutLocators.sidenav
                                          .secondChanceLeadsBadge
                                      }
                                      badgeTitle='Number of new open second chance leads'
                                      useCountQuery={
                                        useSecondChanceLeadsCountQuery
                                      }
                                      getFilters={
                                        getSecondChanceLeadBadgeFilters
                                      }
                                    />
                                  )}
                                  <LoadingIndicator show={!showSclBadge} />
                                </SideNav.Item>
                              </Link>
                            </RequirePermissions>
                          )}
                          {!!virtualAgentFeatures.followUps && (
                            <RequirePermissions
                              permissions={
                                permissionSets.general.FOLLOW_UPS.VIRTUAL_AGENT
                              }
                              permissionsMatch='all'
                            >
                              <Link
                                to='/urgentfollowups/virtualagentfollowups'
                                onClick={() =>
                                  navigate(
                                    '/urgentfollowups/virtualagentfollowups',
                                  )
                                }
                              >
                                <SideNav.Item
                                  active={isActive(
                                    '/urgentfollowups/virtualagentfollowups',
                                  )}
                                  id={
                                    mainLayoutLocators.sidenav
                                      .virtualAgentsCallsPageNavBtn
                                  }
                                >
                                  Voice Agents
                                  <UrgentFollowUpsBadge
                                    id={
                                      mainLayoutLocators.sidenav
                                        .vaFollowUpsBadge
                                    }
                                    badgeTitle='Number of new open voice agent followups'
                                    useCountQuery={
                                      useVirtualAgentFollowUpsCountQuery
                                    }
                                    getFilters={
                                      getVirtualAgentFollowUpsBadgeCountFilters
                                    }
                                  />
                                </SideNav.Item>
                              </Link>
                            </RequirePermissions>
                          )}
                          {!!featureVoicemailFollowUps && (
                            <RequirePermissions
                              permissions={
                                permissionSets.general.FOLLOW_UPS.VOICEMAIL
                              }
                              permissionsMatch='all'
                            >
                              <Link
                                to='/urgentfollowups/voicemails'
                                onClick={() =>
                                  navigate('/urgentfollowups/voicemails')
                                }
                              >
                                <SideNav.Item
                                  active={isActive(
                                    '/urgentfollowups/voicemails',
                                  )}
                                  id={
                                    mainLayoutLocators.sidenav
                                      .voicemailsPageNavBtn
                                  }
                                >
                                  Voicemails
                                  {/* TODO: Add voicemail count badge once requirements are defined. */}
                                </SideNav.Item>
                              </Link>
                            </RequirePermissions>
                          )}
                        </SideNav>
                      </SideNav.Item>
                    )}
                    {virtualAgentFeatures.configuration &&
                      featureVoiceAgentReporting && (
                        <SideNav.Item
                          collapsible={{ expanded: true }}
                          active={isActive(paths.voiceAgentDashboard)}
                        >
                          <span className={styles.NoBoldExpanded}>Reports</span>
                          <SideNav>
                            <Link
                              to={paths.voiceAgentDashboard}
                              onClick={() =>
                                navigate(paths.voiceAgentDashboard)
                              }
                            >
                              <SideNav.Item
                                active={isActive(paths.voiceAgentDashboard)}
                              >
                                Voice Agent Dashboard
                              </SideNav.Item>
                            </Link>
                          </SideNav>
                        </SideNav.Item>
                      )}
                  </>
                )}
                {isSettings && (
                  <>
                    <RequirePermissions
                      permissions={permissionSets.admin.USER_MANAGEMENT}
                      permissionsMatch='any'
                    >
                      <Link to={paths.userManagement}>
                        <SideNav.Item
                          active={isActive(`/${paths.userManagement}`)}
                          id={
                            mainLayoutLocators.sidenav.userManagementPageNavBtn
                          }
                        >
                          User Management
                        </SideNav.Item>
                      </Link>
                    </RequirePermissions>
                    <RequirePermissions
                      permissions={permissionSets.admin.TELECOM_WORKFLOWS}
                      permissionsMatch='any'
                    >
                      <SideNav.Item
                        collapsible={{
                          expanded: true,
                        }}
                        active={
                          isActive(`/${paths.workflows}`) ||
                          isActive(`/${paths.queueManagement}`) ||
                          isActive(`/${paths.presetDaysTimes}`)
                        }
                      >
                        <span className={styles.NoBoldExpanded}>
                          Routing Workflows
                        </span>
                        <SideNav>
                          <Link
                            onClick={() => navigate(`/${paths.workflows}`)}
                            to={paths.workflows}
                          >
                            <SideNav.Item
                              active={isActive(`/${paths.workflows}`)}
                              id={
                                mainLayoutLocators.sidenav
                                  .workflowManagementPageNavBtn
                              }
                            >
                              Workflows
                            </SideNav.Item>
                          </Link>
                          <Link
                            onClick={() =>
                              navigate(`/${paths.presetDaysTimes}`)
                            }
                            to={paths.presetDaysTimes}
                          >
                            <SideNav.Item
                              active={isActive(`/${paths.presetDaysTimes}`)}
                              id={
                                mainLayoutLocators.sidenav
                                  .presetDaysTimesPageNavBtn
                              }
                            >
                              Preset Days/Times
                            </SideNav.Item>
                          </Link>
                          {!!featureQueueManagementPage && (
                            <Link
                              onClick={() =>
                                navigate(`/${paths.queueManagement}`)
                              }
                              to={paths.queueManagement}
                            >
                              <SideNav.Item
                                active={isActive(`/${paths.queueManagement}`)}
                                id={
                                  mainLayoutLocators.sidenav
                                    .queueManagementPageNavBtn
                                }
                              >
                                Queues
                              </SideNav.Item>
                            </Link>
                          )}
                        </SideNav>
                      </SideNav.Item>
                    </RequirePermissions>

                    {featureRecordingDefaultsConfiguration && (
                      <RequirePermissions
                        permissions={permissionSets.admin.RECORDING_SETTINGS}
                        permissionsMatch='all'
                      >
                        <SideNav.Item
                          collapsible={{
                            expanded: true,
                          }}
                          active={isActive(`/${paths.transfersRecording}`)}
                        >
                          <span className={styles.NoBoldExpanded}>
                            Recording Defaults
                          </span>
                          <SideNav>
                            {!!featureGlobalRecordingOverridesEnabled && (
                              <Link
                                onClick={() =>
                                  navigate(`/${paths.globalRecording}`)
                                }
                                to={paths.globalRecording}
                              >
                                <SideNav.Item
                                  active={isActive(`/${paths.globalRecording}`)}
                                  id={
                                    mainLayoutLocators.sidenav
                                      .globalRecordingPageNavBtn
                                  }
                                >
                                  Global Defaults
                                </SideNav.Item>
                              </Link>
                            )}
                            {!isGlobalOverrideEnabled && (
                              <>
                                <Link
                                  onClick={() =>
                                    navigate(`/${paths.queueRecording}`)
                                  }
                                  to={paths.queueRecording}
                                >
                                  <SideNav.Item
                                    active={isActive(
                                      `/${paths.queueRecording}`,
                                    )}
                                    id={
                                      mainLayoutLocators.sidenav
                                        .queueRecordingPageNavBtn
                                    }
                                  >
                                    Queue Defaults
                                  </SideNav.Item>
                                </Link>
                                {featureUserRecordingDefaultsConfiguration && (
                                  <Link
                                    onClick={() =>
                                      navigate(`/${paths.individualRecording}`)
                                    }
                                    to={paths.individualRecording}
                                  >
                                    <SideNav.Item
                                      active={isActive(
                                        `/${paths.individualRecording}`,
                                      )}
                                      id={
                                        mainLayoutLocators.sidenav
                                          .individualRecordingPageNavBtn
                                      }
                                    >
                                      Individual Defaults
                                    </SideNav.Item>
                                  </Link>
                                )}
                              </>
                            )}
                            {!featureGlobalRecordingOverridesEnabled && (
                              <Link
                                onClick={() =>
                                  navigate(`/${paths.transfersRecording}`)
                                }
                                to={paths.transfersRecording}
                              >
                                <SideNav.Item
                                  active={isActive(
                                    `/${paths.transfersRecording}`,
                                  )}
                                  id={
                                    mainLayoutLocators.sidenav
                                      .transferRecordingPageNavBtn
                                  }
                                >
                                  Transfer Defaults
                                </SideNav.Item>
                              </Link>
                            )}
                            <Link
                              onClick={() =>
                                navigate(`/${paths.recordingExclusionList}`)
                              }
                              to={paths.recordingExclusionList}
                            >
                              <SideNav.Item
                                active={isActive(
                                  `/${paths.recordingExclusionList}`,
                                )}
                                id={
                                  mainLayoutLocators.sidenav
                                    .exclusionListPageNavBtn
                                }
                              >
                                Exclusion List
                              </SideNav.Item>
                            </Link>
                          </SideNav>
                        </SideNav.Item>
                      </RequirePermissions>
                    )}

                    {featureManageAccounts && (
                      <RequirePermissions
                        permissions={
                          permissionSets.admin.TELECOM_MANAGE_ACCOUNT
                        }
                        permissionsMatch='all'
                      >
                        <Link to={paths.manageAccounts}>
                          <SideNav.Item
                            active={isActive(`/${paths.manageAccounts}`)}
                          >
                            Manage Caller IDs
                          </SideNav.Item>
                        </Link>
                      </RequirePermissions>
                    )}
                    {virtualAgentFeatures.configuration && (
                      <RequirePermissions
                        permissions={permissionSets.admin.VIRTUAL_AGENT}
                        permissionsMatch='any'
                      >
                        <Link to={paths.virtualAgents}>
                          <SideNav.Item
                            active={isActive(`/${paths.virtualAgents}`)}
                            id={
                              mainLayoutLocators.sidenav
                                .virtualAgentSettingPageNavBtn
                            }
                          >
                            Voice Agents
                          </SideNav.Item>
                        </Link>
                      </RequirePermissions>
                    )}
                    <RequirePermissions
                      permissions={permissionSets.admin.FEATURE_MANAGEMENT}
                      permissionsMatch='any'
                    >
                      <SideNav.Item collapsible={{ expanded: true }}>
                        <span className={styles.NoBoldExpanded}>
                          Feature Management
                        </span>
                        <SideNav>
                          <RequirePermissions
                            permissions={
                              permissionSets.admin
                                .FEATURE_MANAGEMENT_CALL_ANALYSIS
                            }
                            permissionsMatch='any'
                          >
                            <Link
                              onClick={() =>
                                navigate(`/${paths.fmCallAnalysis}`)
                              }
                              to={paths.fmCallAnalysis}
                            >
                              <SideNav.Item
                                active={isActive(`/${paths.fmCallAnalysis}`)}
                                id={
                                  mainLayoutLocators.sidenav
                                    .callAnalysisPageNavBtn
                                }
                              >
                                Call Analysis
                              </SideNav.Item>
                            </Link>
                          </RequirePermissions>
                          {!!featureDataIntegrationEnabled && (
                            <RequirePermissions
                              permissions={
                                permissionSets.admin.FEATURE_MANAGEMENT
                              }
                              permissionsMatch='any'
                            >
                              <Link
                                onClick={() =>
                                  navigate(`/${paths.dataIntegration}`)
                                }
                                to={paths.dataIntegration}
                              >
                                <SideNav.Item
                                  active={isActive(`/${paths.dataIntegration}`)}
                                  id={
                                    mainLayoutLocators.sidenav
                                      .dataIntegrationPageNavBtn
                                  }
                                >
                                  Data Integration
                                </SideNav.Item>
                              </Link>
                            </RequirePermissions>
                          )}
                          {!!featureInboxTaskCardSettingsEnabled && (
                            <RequirePermissions
                              permissions={
                                permissionSets.admin.FEATURE_MANAGEMENT
                              }
                              permissionsMatch='any'
                            >
                              <Link
                                onClick={() =>
                                  navigate(`/${paths.inboxTaskCards}`)
                                }
                                to={paths.inboxTaskCards}
                              >
                                <SideNav.Item
                                  active={isActive(`/${paths.inboxTaskCards}`)}
                                  id={
                                    mainLayoutLocators.sidenav
                                      .inboxTaskCardsPageNavBtn
                                  }
                                >
                                  Inbox Task Cards
                                </SideNav.Item>
                              </Link>
                            </RequirePermissions>
                          )}
                        </SideNav>
                      </SideNav.Item>
                    </RequirePermissions>
                  </>
                )}
              </SideNav>
              {!isSettings && user?.personalLine?.phoneNumber && (
                <SideNav>
                  <Link to='/personalsettings'>
                    <SideNav.Item
                      active={isActive('/personalsettings')}
                      className='p-b-1'
                    >
                      <Stack spacing={1}>
                        <Stack.Item className='lh-1'>
                          <PersonalSettingsIcon />
                        </Stack.Item>

                        <Stack.Item>My Phone Settings</Stack.Item>
                      </Stack>
                    </SideNav.Item>
                  </Link>
                  {e911AddressState.e911Address && (
                    <Stack direction='column' className='p-1'>
                      <Stack.Item>
                        <Eyebrow>911 Address</Eyebrow>
                      </Stack.Item>
                      <Stack.Item className='m-l--half'>
                        <FeatureErrorBoundary featureName='E911 Address Bar'>
                          <E911AddressBar />
                        </FeatureErrorBoundary>
                      </Stack.Item>
                    </Stack>
                  )}
                </SideNav>
              )}
            </Sidebar>
          }
        >
          <LoadingOverlay loading={outletLoading}>
            <Outlet />
          </LoadingOverlay>

          {isManagerAssistEnabled && (
            <RequirePermissions
              permissions={permissionSets.manager.MANAGER_ASSIST}
              permissionsMatch='all'
            >
              <ModuleErrorBoundary moduleName='ManagerAssist'>
                <ManagerAssistChat
                  isActive={isManagerAssistActive}
                  onClose={handleToggleIsManagerAssistActive}
                />
              </ModuleErrorBoundary>
            </RequirePermissions>
          )}
        </Page>
      </Frame>
      <ModuleErrorBoundary moduleName='CallBar' size='compact'>
        <CallBarManager />
      </ModuleErrorBoundary>
      <ModuleErrorBoundary moduleName='IncomingCallToasts' size='compact'>
        <IncomingCallToastsGroup />
      </ModuleErrorBoundary>
      <HiddenFrame />
    </Droppable>
  );
};

export default MainLayout;
